import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {IdName} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {AppointmentRequest, AppointmentRequestRequirements} from '@shared/models/appointment-request.model';
import {FileService} from '@services/file.service';
import {appointmentProofDeliveryMethod} from '@shared/constants/appointment.constants';
import {Appointment, AppointmentArrival, AppointmentNotice} from '@shared/models/appointment.model';
import {PatientHisValidation} from '@shared/models/patient.model';
import {UserLink} from '@shared/models/user-profile.model';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private _appointmentRequestRequirementsModel: AppointmentRequestRequirements;

  constructor(
    private http: HttpClient,
    private fileService: FileService
  ) {
  }

  async getAppointments(
    page: number,
    size: number,
    sort: string,
    pending: boolean,
    startDate?: number,
    endDate?: number,
    centerId?: number,
    serviceId?: number
  ): Promise<Appointment[]> {

    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('pending', pending.toString());

    if (sort) {
      httpParams = httpParams.set('sort', sort);
    }

    if (startDate) {
      httpParams = httpParams.set('startDate', startDate.toString());
    }

    if (endDate) {
      httpParams = httpParams.set('endDate', endDate.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    const url = '/api/v1/appointment';
    return this.http.get<Appointment[]>(url, {params: httpParams}).toPromise();
  }

  async getAppointmentsHttp(
    page: number,
    size: number,
    sort: string,
    pending: boolean,
    startDate?: number,
    endDate?: number,
    centerId?: number,
    serviceId?: number
  ): Promise<HttpResponse<Appointment[]>> {

    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('pending', pending.toString());

    if (sort) {
      httpParams = httpParams.set('sort', sort);
    }

    if (startDate) {
      httpParams = httpParams.set('startDate', startDate.toString());
    }

    if (endDate) {
      httpParams = httpParams.set('endDate', endDate.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    const url = '/api/v1/appointment';
    return this.http.get<Appointment[]>(url, {params: httpParams, observe: 'response'}).toPromise();
  }

  async getAppointmentSlots(
    serviceId: number,
    appointmentReasonId: string,
    collectiveId?: number,
    startDate?: number,
    professionalId?: number,
    centerId?: number,
    timePreferenceId?: number
  ): Promise<AppointmentRequest[]> {

    let httpParams = new HttpParams()
      .set('serviceId', serviceId.toString())
      .set('appointmentReasonId', appointmentReasonId);

    if (collectiveId) {
      httpParams = httpParams.set('collectiveId', collectiveId.toString());
    }

    if (startDate) {
      httpParams = httpParams.set('startDate', startDate.toString());
    }

    if (professionalId) {
      httpParams = httpParams.set('professionalId', professionalId.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (timePreferenceId) {
      httpParams = httpParams.set('timePreferenceId', timePreferenceId.toString());
    }

    const url = '/api/v1/appointment/slots';
    return this.http.get<AppointmentRequest[]>(url, {params: httpParams}).toPromise();
  }

  async setAppointmentSlotReserve(model: AppointmentRequest): Promise<AppointmentRequest> {
    const url = '/api/v1/appointment/slot/reserve';
    return this.http.post<AppointmentRequest>(url, model).toPromise();
  }

  async deleteAppointmentSlotReserve(reserveId: number): Promise<void> {
    const url = `/api/v1/appointment/slot/reserve/${reserveId}`;
    await this.http.delete(url).toPromise();
  }

  async setAppointmentSlotConfirm(model: AppointmentRequest, addCollective = false): Promise<IdName> {
    const url = '/api/v1/appointment/confirm';
    const httpParams = new HttpParams().set('addCollective', addCollective.toString());
    return this.http.post<IdName>(url, model, {params: httpParams}).toPromise();
  }

  async getAppointment(appointmentId: number): Promise<Appointment> {
    const url = `/api/v1/appointment/${appointmentId}`;
    return this.http.get<Appointment>(url).toPromise();
  }

  async setAppointmentCancel(appointmentId: number): Promise<Appointment> {
    const url = `/api/v1/appointment/${appointmentId}`;
    return this.http.delete<Appointment>(url).toPromise();
  }

  async getDownloadInformedConsent(appointmentId: number, icId: number): Promise<void> {
    const url = `/api/v1/appointment/${appointmentId}/ic/${icId}`;
    await this.fileService.downloadPdf(url);
  }

  async getDownloadProof(appointmentId: number): Promise<void> {
    return this.getProof(appointmentId, appointmentProofDeliveryMethod.WEB);
  }

  async getSendProof(appointmentId: number): Promise<void> {
    return this.getProof(appointmentId, appointmentProofDeliveryMethod.EMAIL);
  }

  async getProof(appointmentId: number, proofDeliveryMethod: string): Promise<void> {
    const url = `/api/v1/appointment/${appointmentId}/proof`;
    const httpParams = new HttpParams().set('proofDeliveryMethod', proofDeliveryMethod);
    if (proofDeliveryMethod === appointmentProofDeliveryMethod.WEB) {
      await this.fileService.downloadPdf(url, httpParams);
    } else if (proofDeliveryMethod === appointmentProofDeliveryMethod.EMAIL) {
      await this.http.get(url, {params: httpParams}).toPromise();
    }
  }

  async getCheckExistingPatientAndCreateIfDoesNot(model: UserLink): Promise<PatientHisValidation> {
    const url = '/api/v1/appointment/patient/link';
    return this.http.post<PatientHisValidation>(url, model).toPromise();
  }

  async setAppointmentReschedule(model: AppointmentRequest): Promise<IdName> {
    const url = `/api/v1/appointment/${model.oldAppointmentId}/reschedule`;
    return this.http.put<IdName>(url, model).toPromise();
  }

  async setAppointmentConfirmArrival(appointmentId: number, model: AppointmentArrival): Promise<IdName> {
    const url = `/api/v1/appointment/${appointmentId}/confirm-arrival`;
    return this.http.put<IdName>(url, model).toPromise();
  }

  async setAppointmentConfirmAttendance(appointmentId: number): Promise<IdName> {
    const url = `/api/v1/appointment/${appointmentId}/confirm-attendance`;
    return this.http.put<IdName>(url, {}).toPromise();
  }

  async getCenters(collectiveId?: number, serviceId?: number, professionalId?: number, internalSchedule?: boolean, externalSchedule?: boolean): Promise<IdName[]> {
    const url = '/api/v1/center';
    let httpParams = new HttpParams();
    if (collectiveId) {
      httpParams = httpParams.set('collectiveId', collectiveId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    if (professionalId) {
      httpParams = httpParams.set('professionalId', professionalId.toString());
    }

    if (internalSchedule) {
      httpParams = httpParams.set('internalSchedule', internalSchedule.toString());
    }

    if (externalSchedule) {
      httpParams = httpParams.set('externalSchedule', externalSchedule.toString());
    }

    return this.http.get<IdName[]>(url, {params: httpParams}).toPromise();
  }

  async getServices(collectiveId?: number, centerId?: number, professionalId?: number, internalSchedule?: boolean, externalSchedule?: boolean): Promise<IdName[]> {
    const url = '/api/v1/service';
    let httpParams = new HttpParams();
    if (collectiveId) {
      httpParams = httpParams.set('collectiveId', collectiveId.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (professionalId) {
      httpParams = httpParams.set('professionalId', professionalId.toString());
    }

    if (internalSchedule) {
      httpParams = httpParams.set('internalSchedule', internalSchedule.toString());
    }

    if (externalSchedule) {
      httpParams = httpParams.set('externalSchedule', externalSchedule.toString());
    }

    return this.http.get<IdName[]>(url, {params: httpParams}).toPromise();
  }

  async getProfessionals(collectiveId?: number, centerId?: number, serviceId?: number, internalSchedule?: boolean, externalSchedule?: boolean): Promise<IdName[]> {
    const url = '/api/v1/professional';
    let httpParams = new HttpParams();
    if (collectiveId) {
      httpParams = httpParams.set('collectiveId', collectiveId.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    if (internalSchedule) {
      httpParams = httpParams.set('internalSchedule', internalSchedule.toString());
    }

    if (externalSchedule) {
      httpParams = httpParams.set('externalSchedule', externalSchedule.toString());
    }

    return this.http.get<IdName[]>(url, {params: httpParams}).toPromise();
  }

  async getAppointmentReasons(collectiveId?: number, centerId?: number, serviceId?: number, professionalId?: number, internalSchedule?: boolean, externalSchedule?: boolean): Promise<IdName[]> {
    const url = '/api/v1/appointment/reasons';
    let httpParams = new HttpParams();
    if (collectiveId) {
      httpParams = httpParams.set('collectiveId', collectiveId.toString());
    }

    if (centerId) {
      httpParams = httpParams.set('centerId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('serviceId', serviceId.toString());
    }

    if (professionalId) {
      httpParams = httpParams.set('professionalId', professionalId.toString());
    }

    if (internalSchedule) {
      httpParams = httpParams.set('internalSchedule', internalSchedule.toString());
    }

    if (externalSchedule) {
      httpParams = httpParams.set('externalSchedule', externalSchedule.toString());
    }

    return this.http.get<IdName[]>(url, {params: httpParams}).toPromise();
  }

  async getCollectives(allInsurances?: boolean, isPrivate?: boolean): Promise<IdName[]> {
    const url = '/api/v1/insurance';
    let httpParams = new HttpParams();
    if (allInsurances) {
      httpParams = httpParams.set('allInsurances', allInsurances.toString());
    }

    if (isPrivate) {
      httpParams = httpParams.set('isPrivate', isPrivate.toString());
    }

    return this.http.get<IdName[]>(url, {params: httpParams}).toPromise();
  }

  setAppointmentRequestRequirementsModel(model: AppointmentRequestRequirements): void {
    this._appointmentRequestRequirementsModel = model;
  }

  getAppointmentRequestRequirementsModel(): AppointmentRequestRequirements {
    return this._appointmentRequestRequirementsModel;
  }

  getAppointmentVouchers(appointmentId: number): Promise<IdName[]> {
    const url = `/api/v1/appointment/${appointmentId}/vouchers`;
    return this.http.get<IdName[]>(url).toPromise();
  }

  async getAppointmentAlerts(centerId?: number, serviceId?: number, insuranceId?: number, reasonId?: number, scheduleId?: number): Promise<AppointmentNotice[]> {
    const url = '/api/v1/appointment/notices';
    let httpParams = new HttpParams();
    if (centerId) {
      httpParams = httpParams.set('centId', centerId.toString());
    }

    if (serviceId) {
      httpParams = httpParams.set('servId', serviceId.toString());
    }

    if (insuranceId) {
      httpParams = httpParams.set('insuranceId', insuranceId.toString());
    }

    if (reasonId) {
      httpParams = httpParams.set('benefitId', reasonId.toString());
    }

    if (scheduleId) {
      httpParams = httpParams.set('scheId', scheduleId.toString());
    }

    return this.http.get<AppointmentNotice[]>(url, {params: httpParams}).toPromise();
  }
}
